/**
 * Who a marketing send reaches, and why everyone else is left out — the ONE
 * rule the email newsletter applies to its preview, its send and each
 * batch's send-time re-check, and the `/campaigns` audiences matrix shows
 * (email-newsletter-editions, 2026-10-18;
 * build-output/architecture/email-campaigns.md → "Audiences").
 *
 * Three layers, each a pure function of a {@link MarketingAudienceFacts}
 * object that ONE SQL statement reads
 * (`@layer/canonical-actions/marketing-audience-sql`):
 *
 *   1. the LIFECYCLE — member, trial, win-back or lead — from real activity
 *      inside a {@link MARKETING_LAPSE_DAYS}-day window (Mario, 2026-10-17);
 *   2. the GOAL — what the member said they want, from the consultation form,
 *      the webchat onboarding answer and a few tags;
 *   3. REACHABILITY per channel — the member's own consent first, then
 *      whether the channel can reach them at all.
 *
 * The SQL returns raw facts and every rule lives here, so the placeholder,
 * goal and internal-address rules have no SQL copy to drift from.
 *
 * NOT the funnel stage. `classifyMemberStage` (`member-stage.ts`) answers
 * "lead, trial or customer" from lifetime spend and sessions with no time
 * window, and counts Codex consolidation payments as spend; a marketing
 * audience needs recency, so a customer who has not trained since the
 * summer is a win-back here. The two answer different questions and neither
 * replaces the other.
 *
 * A leaf: it imports only other import-free leaves, so the dashboard page
 * bundles it through a Vite alias and `@layer/saga-runtime` may read it at
 * module scope.
 */

import { cleanProfileFieldValue } from "./member-profile-gaps.js";
import { isPlaceholderEmail } from "./placeholder-email.js";
import { isUsPhoneNumber } from "./us-phone-number.js";

// ---------------------------------------------------------------------------
// Vocabulary.
// ---------------------------------------------------------------------------

/**
 * "Recent" means within this many days: a session, a booking or a real
 * purchase inside the window keeps a member in their lifecycle; nothing
 * inside it makes a past customer a win-back (Mario, 2026-10-17).
 */
export const MARKETING_LAPSE_DAYS = 60;

/**
 * The four lifecycles, in the order a member is tested against them. The
 * first that applies wins:
 *
 *   - `member`: an active membership, a usable non-intro package from the
 *     paid family, a paid non-intro package bought inside the window, or at
 *     least two completed sessions with one inside the window or one still
 *     to come;
 *   - `trial`: a future booking (at most one session so far), a usable gift
 *     package (the free family: comp, trial, transfer — free means trial, as
 *     in the stage rule), or the intro offer (or at most one session) with a
 *     session or a real payment inside the window;
 *   - `winback`: paid (net of refunds), trained or held the intro offer, and
 *     none of the above;
 *   - `lead`: everyone else.
 */
export const MARKETING_LIFECYCLES = ["member", "trial", "winback", "lead"] as const;
export type MarketingLifecycle = (typeof MARKETING_LIFECYCLES)[number];

/**
 * The goals, in PRECEDENCE order: a member who named several is filed under
 * the first that matches. Injury comes first because its block asks the
 * member to tell the studio before booking — the safety message must win.
 * `toning` (the most common answer on file, 2026-10-18) is its own bucket so
 * an edition may write for it; {@link MARKETING_GOAL_BODY_FALLBACK} sends a
 * toning member the muscle block when the edition has no toning one.
 * Skin rejuvenation maps to no goal: the claims posture rules out skin
 * claims, so there is nothing goal-specific to say.
 */
export const MARKETING_GOALS = [
  "rehab_injury",
  "weight_loss",
  "toning",
  "muscle",
  "general",
] as const;
export type MarketingGoal = (typeof MARKETING_GOALS)[number];

/**
 * The body a goal falls back to when an edition wrote none for it, tried in
 * order after the goal's own. Every chain ends at `general`, which every
 * edition must carry for every lifecycle it sends to.
 */
export const MARKETING_GOAL_BODY_FALLBACK: Readonly<
  Record<MarketingGoal, ReadonlyArray<MarketingGoal>>
> = {
  rehab_injury: ["general"],
  weight_loss: ["general"],
  toning: ["muscle", "general"],
  muscle: ["general"],
  general: [],
};

/** The channels a marketing send can use. */
export const MARKETING_CHANNELS = ["email", "whatsapp"] as const;
export type MarketingChannel = (typeof MARKETING_CHANNELS)[number];

/**
 * Why a member is left out, in the order they are tested: a member is
 * counted under the FIRST reason that applies. The member's own choices
 * come before what the channel can do.
 *
 *   - `member_inactive` — the member row is not `active` (only reachable
 *     through the send-time re-check of a frozen recipient);
 *   - `not_test_recipient` — a TEST edition goes only to members tagged
 *     {@link MARKETING_TEST_RECIPIENT_TAG_SLUG}; everyone else is left out
 *     under this reason (never used for a real edition);
 *   - `not_in_audience_tags` — an edition sent to picked tags goes only to
 *     members carrying ANY of them; everyone else is left out under this
 *     reason (email-newsletter-tag-audience, 2026-10-31; never with a test
 *     edition, which goes to `testing` alone);
 *   - `internal_or_test` — staff, a test account or "do not contact"
 *     ({@link MARKETING_INTERNAL_TAG_SLUGS}, {@link isInternalEmailAddress});
 *     for a test edition, "do not contact" only;
 *   - `marketing_opted_out` — `flow_opt_outs.all_marketing`;
 *   - `newsletter_opted_out` — `flow_opt_outs.newsletter` (email only until
 *     the WhatsApp newsletter moves onto this module);
 *   - `site_consent_withdrawn` — `consent.marketing` is false (the website's
 *     marketing toggle);
 *   - `channel_opted_out` — `email_opt_in` / `whatsapp_opt_in` is false (the
 *     profile's "Contact via" boxes);
 *   - `no_address` — no email address, or one Resend would refuse
 *     ({@link isSendableEmailAddress}); no sendable WhatsApp number;
 *   - `placeholder_email` — a channel-signup placeholder address;
 *   - `bounced_or_complained` — the growing pause's marketing bar keeps the
 *     address out: a spam complaint (ever), an account that does not exist,
 *     or a bounce whose pause still runs (deliverability-ladder, 2026-10-31;
 *     before, any bounce kept it out for good);
 *   - `number_paused` — the WhatsApp number the send would use is on the
 *     growing pause after Meta reported it not on WhatsApp
 *     (deliverability-ladder, 2026-10-31; WhatsApp only);
 *   - `us_number` — the WhatsApp number the send would use is a US number
 *     and the studio leaves those out ({@link WhatsAppMarketingPolicy}):
 *     Meta does not deliver marketing templates to them
 *     (deliverability-ladder D7, 2026-10-31; WhatsApp only);
 *   - `joined_recently` — the member joined less than
 *     {@link NEWSLETTER_JOIN_HOLD_MS} before an email edition goes; it waits
 *     for the next one (email, at send time only);
 *   - `duplicate_number` — another member already holds this WhatsApp
 *     number in the same send.
 */
export const MARKETING_EXCLUSION_REASONS = [
  "member_inactive",
  "not_test_recipient",
  "not_in_audience_tags",
  "internal_or_test",
  "marketing_opted_out",
  "newsletter_opted_out",
  "site_consent_withdrawn",
  "channel_opted_out",
  "no_address",
  "placeholder_email",
  "bounced_or_complained",
  "number_paused",
  "us_number",
  "joined_recently",
  "duplicate_number",
] as const;
export type MarketingExclusionReason = (typeof MARKETING_EXCLUSION_REASONS)[number];

/** The `flow_opt_outs` key the email newsletter's unsubscribe link writes. */
export const NEWSLETTER_FLOW_NAME = "newsletter";

/**
 * newsletter-welcome (2026-10-21) — how long a member who has just joined
 * waits for their first edition. Mario: no more than one email in the 24
 * hours after someone signs up, and a newsletter joiner's one email is the
 * welcome (`newsletter_welcome`). So an edition holds back anyone who joined
 * less than this long before it goes (`joined_recently`) — a newsletter joiner
 * or anyone else new that day — and they get the next one.
 */
export const NEWSLETTER_JOIN_HOLD_MS = 24 * 60 * 60 * 1000;

/**
 * The tag on a staff member's own member profile. A staff profile is no
 * marketing audience, and never gets the Google review link after a tapped 3
 * (session-rating-review-staff, 2026-11-03).
 */
export const MARKETING_STAFF_TAG_SLUG = "admin";

/**
 * Tags that mark a member as staff, a test account or someone never to be
 * marketed to. Matched on the slug.
 */
export const MARKETING_INTERNAL_TAG_SLUGS = [MARKETING_STAFF_TAG_SLUG, "testing", "do-not-contact"] as const;

/** "Do not contact": wins over everything, a test edition included. */
export const MARKETING_DO_NOT_CONTACT_TAG_SLUG = "do-not-contact";

/**
 * The tag that puts a member in a TEST edition's audience — and nobody else
 * is in it. A test edition exists to reach staff and test addresses, so for
 * it the internal rules (staff tags, the studio's own domains) do not apply;
 * "do not contact" and every consent rule still do.
 */
export const MARKETING_TEST_RECIPIENT_TAG_SLUG = "testing";

/**
 * The studio's own and the platform's domains: an address here belongs to a
 * colleague, not a member. `example.*` catches test rows.
 */
export const MARKETING_INTERNAL_EMAIL_DOMAINS = [
  "e-pulsive.co.uk",
  "e-pulsive.ch",
  "hercie.ai",
  "useclavis.ai",
] as const;

/**
 * Tags that count as goal evidence, by goal. A tag decides the goal only when
 * no answer names one ({@link classifyMarketingGoal}): `campaign-weight-loss`
 * records the ad that brought someone in, not what they said they want. The
 * injury tag is the exception — injury evidence always counts.
 */
export const MARKETING_GOAL_TAG_SLUGS: Readonly<Record<string, MarketingGoal>> = {
  "injury-back": "rehab_injury",
  "goal-fat-loss": "weight_loss",
  "campaign-weight-loss": "weight_loss",
};

// ---------------------------------------------------------------------------
// Facts.
// ---------------------------------------------------------------------------

/**
 * What the rules read about one member, at the moment they are asked. Every
 * field is a raw value; nothing here is already classified.
 */
export interface MarketingAudienceFacts {
  readonly memberId: string;
  /** `members.status`. Only `active` members are marketed to. */
  readonly status: string;
  /** `members.email` as stored (nullable column). */
  readonly email: string | null;
  /** `members.display_name`. */
  readonly displayName: string | null;
  /** `metadata.flow_opt_outs`, as stored (an object, or anything else). */
  readonly flowOptOuts: unknown;
  /** `metadata.email_opt_in`, as stored. */
  readonly emailOptIn: unknown;
  /** `metadata.whatsapp_opt_in`, as stored. */
  readonly whatsappOptIn: unknown;
  /** `metadata.consent.marketing`, as stored (the website toggle). */
  readonly siteConsentMarketing: unknown;
  /**
   * The goal answers on file: `metadata.consultation.CF_3_Goal`, `_1`, `_2`,
   * `_3` and `metadata.preferences["Onboarding - Goals"]`, as stored.
   */
  readonly goalAnswers: ReadonlyArray<unknown>;
  /** The member's tag slugs. */
  readonly tagSlugs: ReadonlyArray<string>;
  /** A live membership (`activeMembershipSql`). */
  readonly activeMembership: boolean;
  /** A usable paid-family package outside the intro-offer family (`usablePackageSql`). */
  readonly liveNonIntroPackage: boolean;
  /** A usable free-family package — a comp, trial or transfer grant. */
  readonly liveFreePackage: boolean;
  /** An intro-offer package (`intro%`) in any status. */
  readonly heldIntroOffer: boolean;
  /**
   * Net spend above zero: the canonical `lifetime_spent_minor` expr,
   * refund-netted, cutover consolidation included.
   */
  readonly everPaid: boolean;
  /**
   * The newest real payment that is neither a cutover consolidation row nor
   * covered in full by its own refund rows.
   */
  readonly lastRealPaymentAt: Date | null;
  /** The newest paid-family non-intro package grant that was not revoked. */
  readonly lastNonIntroPurchaseAt: Date | null;
  /** Completed sessions, ever. */
  readonly completedSessions: number;
  /** The start of the latest session that has begun (active or completed). */
  readonly lastSessionAt: Date | null;
  /** A booked session still to come. */
  readonly futureBooking: boolean;
  /** The address has a `bounced` or `complained` delivery event. */
  readonly emailBouncedOrComplained: boolean;
  /**
   * When this member row was made (`members.created_at`): for a newsletter
   * joiner, the moment their welcome went out. The email edition's
   * {@link NEWSLETTER_JOIN_HOLD_MS} runs from it. Deliberately not the earlier
   * `first_known_at` a merge can carry onto the row (migration 230): the
   * welcome went out when THIS row was made.
   */
  readonly createdAt: Date;
  /**
   * The member's WhatsApp number candidates in preference order (binding,
   * vendor link, `members.phone`) — the order the WhatsApp senders use.
   */
  readonly whatsappNumbers: ReadonlyArray<string | null>;
  /**
   * Which of those numbers are on the growing pause at the instant the facts
   * were read (deliverability-ladder, 2026-10-31), in the send's `+` and
   * digits form: Meta reported them not on WhatsApp and nothing has lifted
   * the pause since. The rule every direct WhatsApp send asks.
   */
  readonly pausedWhatsAppNumbers: ReadonlyArray<string>;
}

// ---------------------------------------------------------------------------
// Lifecycle.
// ---------------------------------------------------------------------------

const DAY_MS = 86_400_000;

/** The member's lifecycle at `now` (see {@link MARKETING_LIFECYCLES}). */
export function classifyMarketingLifecycle(
  facts: Pick<
    MarketingAudienceFacts,
    | "activeMembership"
    | "liveNonIntroPackage"
    | "liveFreePackage"
    | "heldIntroOffer"
    | "everPaid"
    | "lastRealPaymentAt"
    | "lastNonIntroPurchaseAt"
    | "completedSessions"
    | "lastSessionAt"
    | "futureBooking"
  >,
  now: Date,
): MarketingLifecycle {
  const since = now.getTime() - MARKETING_LAPSE_DAYS * DAY_MS;
  const within = (d: Date | null): boolean => d !== null && d.getTime() >= since;
  const recentSession = within(facts.lastSessionAt);
  // An orphan refund (no `parent_payment_id`) offsets no one charge, so a
  // recent payment counts only while the member's net spend is above zero.
  const recentPayment = facts.everPaid && within(facts.lastRealPaymentAt);
  if (
    facts.activeMembership ||
    facts.liveNonIntroPackage ||
    within(facts.lastNonIntroPurchaseAt) ||
    (facts.completedSessions >= 2 && (recentSession || facts.futureBooking))
  ) {
    return "member";
  }
  if (
    facts.futureBooking ||
    facts.liveFreePackage ||
    ((facts.heldIntroOffer || facts.completedSessions <= 1) && (recentSession || recentPayment))
  ) {
    return "trial";
  }
  if (facts.everPaid || facts.completedSessions >= 1 || facts.heldIntroOffer) {
    return "winback";
  }
  return "lead";
}

// ---------------------------------------------------------------------------
// Goal.
// ---------------------------------------------------------------------------

/**
 * The consultation form's own answers (`extract-consultation-form.ts`
 * humanises its enum `weight_loss`, `body_toning`, `muscle_growth`,
 * `skin_rejuvenation`, `injury_rehab`), matched exactly first.
 */
const FORM_GOAL_ANSWERS: Readonly<Record<string, MarketingGoal | null>> = {
  "weight loss": "weight_loss",
  "body toning": "toning",
  "muscle growth": "muscle",
  "skin rejuvenation": null,
  "injury rehab": "rehab_injury",
};

/**
 * Free-text patterns, read on word boundaries after `_` and `-` became
 * spaces (a raw `injury_rehab` is the form's own answer). The interim lane
 * SQL matched bare substrings, so "close" read as "lose" and "paint" as
 * "pain"; the first patterns here read "gain weight", "weight training" and
 * "don't want to lose muscle" as weight loss, and "build confidence" as
 * muscle, and the next "weight gain" as weight loss. Tested in precedence order like the buckets themselves. Injury
 * reads wide on purpose: its block asks the member to tell the studio
 * before booking, so a false match costs a line of caution, a miss costs
 * the warning.
 */
const GOAL_PATTERNS: ReadonlyArray<readonly [MarketingGoal, RegExp]> = [
  [
    "rehab_injury",
    /\b(injur(?:y|ies|ed)|rehab\w*|recover(?:y|ing)?|accident|pains?|painful|aches?|physio\w*|posture|joints?|sciatica|arthritis|osteo\w*|scoliosis|hernia\w*|herniated|slipped dis[ck]|dis[ck]s?|spine|spinal|surgery|operation|post ?natal|postpartum|pregnan\w*|ligaments?|tendons?|tendin\w*|sprain\w*|fractur\w*|bad back|knees?|(?:hip|knee) replacement|(?:back|neck|shoulder|hip|knee) (?:problems?|issues?|trouble))\b/u,
  ],
  [
    "weight_loss",
    /\b(weight ?loss|los(?:e|ing) (?:(?:some|the|my|a bit of|a little) )?(?:weight|fat|pounds|lbs|kilos?|kgs?|stone|inches|belly|tummy|body fat)|fat ?(?:loss|burn\w*)|burn(?:ing)? (?:body )?fat|slim(?:ming|mer)?|get lean|leaner|belly|tummy|waist\w*|adipose|overweight|weight|fat)\b/u,
  ],
  ["toning", /\b(ton(?:e|ed|ing)|tighten(?:ing)?|definition|firm(?:er|ing)?|sculpt\w*)\b/u],
  [
    "muscle",
    /\b(muscles?|muscular|strength|stronger|hypertrophy|bulk(?:ing|ier)?|build(?:ing)? (?:up )?(?:muscle|strength|mass|size)|(?:gain(?:ing)?|put(?:ting)? on) (?:some )?(?:healthy )?(?:weight|mass|muscle|size)|(?<!\b(?:avoid|prevent|stop)(?:ing)? |\bno )weight ?gain(?:ing)?|weights|weight ?(?:training|lifting))\b/u,
  ],
];

/**
 * Phrases that use a weight-loss word for another goal, taken out before the
 * weight-loss pattern reads the answer: gaining or putting on weight, either
 * way round ("weight gain" too, but not "avoid weight gain", which is weight
 * loss; 2026-10-19 review), weight training, "weights", losing muscle.
 */
const NOT_WEIGHT_LOSS =
  /\b(?:gain(?:ing)?|put(?:ting)? on|build(?:ing)?) (?:some )?(?:healthy )?(?:weight|mass)\b|\b(?<!\b(?:avoid|prevent|stop)(?:ing)? |\bno )weight ?gain(?:ing)?\b|\bweights\b|\bweight ?(?:training|lifting|bearing)\b|\blos(?:e|ing) (?:my |any )?muscles?\b/gu;

/** One goal answer → the goals it names (deduplicated, in precedence order). */
function goalsInAnswer(raw: unknown): ReadonlyArray<MarketingGoal> {
  const cleaned = cleanProfileFieldValue(raw);
  if (cleaned === undefined) return [];
  const found = new Set<MarketingGoal>();
  // A multi-select answer reads back as "Weight Loss, Muscle Growth".
  for (const part of cleaned.split(",")) {
    const text = part.toLowerCase().replace(/[_-]+/gu, " ").replace(/\s+/gu, " ").trim();
    if (text.length === 0) continue;
    if (Object.prototype.hasOwnProperty.call(FORM_GOAL_ANSWERS, text)) {
      const g = FORM_GOAL_ANSWERS[text];
      if (g !== null && g !== undefined) found.add(g);
      continue;
    }
    for (const [goal, re] of GOAL_PATTERNS) {
      const read = goal === "weight_loss" ? text.replace(NOT_WEIGHT_LOSS, " ") : text;
      if (re.test(read)) found.add(goal);
    }
  }
  return MARKETING_GOALS.filter((g) => found.has(g));
}

/**
 * The member's goal: the first in precedence order that the answers name.
 * A tag decides only when no answer names a goal — except the injury tag,
 * which always counts ({@link MARKETING_GOAL_TAG_SLUGS}).
 */
export function classifyMarketingGoal(
  facts: Pick<MarketingAudienceFacts, "goalAnswers" | "tagSlugs">,
): MarketingGoal {
  const found = new Set<MarketingGoal>();
  for (const answer of facts.goalAnswers) {
    for (const g of goalsInAnswer(answer)) found.add(g);
  }
  const answered = found.size > 0;
  for (const slug of facts.tagSlugs) {
    const g = Object.prototype.hasOwnProperty.call(MARKETING_GOAL_TAG_SLUGS, slug)
      ? MARKETING_GOAL_TAG_SLUGS[slug]
      : undefined;
    if (g !== undefined && (!answered || g === "rehab_injury")) found.add(g);
  }
  return MARKETING_GOALS.find((g) => g !== "general" && found.has(g)) ?? "general";
}

// ---------------------------------------------------------------------------
// Consent.
// ---------------------------------------------------------------------------

/**
 * A member's marketing consent, read from `members.metadata` with literal
 * semantics: an opt-out counts only when it is the JSON boolean `true`, an
 * opt-in box only when it is the JSON boolean `false` — exactly as the
 * marketing flows read them (`readMarketingFlowOptIns`,
 * `isFlowOptedOut`), so an odd stored value never silences anyone and never
 * re-subscribes anyone either. Absent means reachable.
 */
export interface MemberMarketingConsent {
  /** `flow_opt_outs.all_marketing === true` — "stop all marketing". */
  readonly allMarketingOptedOut: boolean;
  /** The `flow_opt_outs` keys set to `true` (`newsletter`, a flow's name, …). */
  readonly flowOptOuts: ReadonlySet<string>;
  /** `email_opt_in === false` — the profile's "Contact via: Email" box. */
  readonly emailOptedOut: boolean;
  /** `whatsapp_opt_in === false` — the profile's "Contact via: WhatsApp" box. */
  readonly whatsappOptedOut: boolean;
  /** `consent.marketing === false` — the website's marketing toggle. */
  readonly siteConsentWithdrawn: boolean;
}

export function readMemberMarketingConsent(
  facts: Pick<
    MarketingAudienceFacts,
    "flowOptOuts" | "emailOptIn" | "whatsappOptIn" | "siteConsentMarketing"
  >,
): MemberMarketingConsent {
  const flowOptOuts = new Set<string>();
  const raw = facts.flowOptOuts;
  if (raw !== null && typeof raw === "object" && !Array.isArray(raw)) {
    for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
      if (value === true) flowOptOuts.add(key);
    }
  }
  return {
    allMarketingOptedOut: flowOptOuts.has("all_marketing"),
    flowOptOuts,
    emailOptedOut: facts.emailOptIn === false,
    whatsappOptedOut: facts.whatsappOptIn === false,
    siteConsentWithdrawn: facts.siteConsentMarketing === false,
  };
}

/** `members.metadata` read as {@link readMemberMarketingConsent} reads its facts. */
function consentInMetadata(metadata: unknown): MemberMarketingConsent {
  const m =
    metadata !== null && typeof metadata === "object" && !Array.isArray(metadata)
      ? (metadata as Readonly<Record<string, unknown>>)
      : {};
  const consent = m["consent"];
  return readMemberMarketingConsent({
    flowOptOuts: m["flow_opt_outs"],
    emailOptIn: m["email_opt_in"],
    whatsappOptIn: m["whatsapp_opt_in"],
    siteConsentMarketing:
      consent !== null && typeof consent === "object" && !Array.isArray(consent)
        ? (consent as Readonly<Record<string, unknown>>)["marketing"]
        : undefined,
  });
}

/**
 * The marketing withdrawals on file in `before` (a member's metadata) that
 * `after` no longer carries, by name: `flow_opt_outs.<key>` for each opt-out
 * (sorted), then `email_opt_in`, `whatsapp_opt_in` and `consent.marketing`.
 * Read literally, as {@link readMemberMarketingConsent} reads them, so an odd
 * value on either side is no withdrawal, and a write that ADDS withdrawals
 * removes none. `update_member` refuses a member's or a background job's
 * write that would remove one (newsletter-resubscribe, 2026-10-23): marketing
 * comes back through the actions made for it — the member's own
 * `set_marketing_opt_out`, signing up again (`resubscribe_newsletter`), or
 * staff and the operator.
 */
export function marketingWithdrawalsRemoved(before: unknown, after: unknown): string[] {
  const was = consentInMetadata(before);
  const now = consentInMetadata(after);
  const removed = [...was.flowOptOuts]
    .filter((key) => !now.flowOptOuts.has(key))
    .sort()
    .map((key) => `flow_opt_outs.${key}`);
  if (was.emailOptedOut && !now.emailOptedOut) removed.push("email_opt_in");
  if (was.whatsappOptedOut && !now.whatsappOptedOut) removed.push("whatsapp_opt_in");
  if (was.siteConsentWithdrawn && !now.siteConsentWithdrawn) removed.push("consent.marketing");
  return removed;
}

/** True for an address on the studio's or the platform's own domains, or `example.*`. */
export function isInternalEmailAddress(email: string): boolean {
  const lower = email.trim().toLowerCase();
  const at = lower.lastIndexOf("@");
  if (at < 0) return false;
  const domain = lower.slice(at + 1);
  if (/(^|\.)example\.[a-z.]+$/u.test(domain)) return true;
  return MARKETING_INTERNAL_EMAIL_DOMAINS.some((d) => domain === d || domain.endsWith(`.${d}`));
}

/**
 * A mailbox Resend will accept — read strictly, the shape common strict
 * validators allow: ASCII only; a local part of letters, digits and
 * `_ ' + - .`, at most 64 long, with no leading, trailing or doubled dot and
 * no trailing `'`; a domain of dotted labels ending in a letters-only
 * top-level domain. Stricter than the RFC on purpose: Resend refuses a whole
 * batch for one malformed address (verified 2026-10-18), so an address it
 * might refuse — `jo..hn@`, `a&b@`, `x@y.c0m`, a zero-width space — never
 * reaches a batch. Expects an address already trimmed of spaces.
 */
export function isSendableEmailAddress(email: string): boolean {
  if (email.length < 6 || email.length > 254) return false;
  const at = email.indexOf("@");
  if (at < 1 || at !== email.lastIndexOf("@")) return false;
  const local = email.slice(0, at);
  const domain = email.slice(at + 1);
  return (
    local.length <= 64 &&
    /^[A-Za-z0-9_'+-]+(?:\.[A-Za-z0-9_'+-]+)*$/u.test(local) &&
    !local.endsWith("'") &&
    /^(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/u.test(domain)
  );
}

/**
 * Spaces trimmed from both ends, and nothing else — exactly what SQL's
 * `btrim(m.email)` strips, so the address sent to is the address the bounce
 * check looked up. Any other padding (a tab, a no-break space) is left in,
 * and {@link isSendableEmailAddress} refuses it. By hand: a `/ +$/` pattern
 * backtracks quadratically on a long run of spaces mid-value.
 */
function trimSpaces(value: string): string {
  let start = 0;
  let end = value.length;
  while (start < end && value[start] === " ") start += 1;
  while (end > start && value[end - 1] === " ") end -= 1;
  return value.slice(start, end);
}

// ---------------------------------------------------------------------------
// Reachability.
// ---------------------------------------------------------------------------

export type MarketingReach<TAddress> =
  | { readonly reachable: true; readonly address: TAddress }
  | { readonly reachable: false; readonly reason: MarketingExclusionReason };

/**
 * The reasons shared by every channel, in order; `null` = none applies. For a
 * test send only members tagged {@link MARKETING_TEST_RECIPIENT_TAG_SLUG} are
 * in, and of the internal rules only "do not contact" applies. With picked
 * tags only members carrying ANY of them are in; every later rule still
 * applies to them.
 */
function commonExclusion(
  facts: MarketingAudienceFacts,
  consent: MemberMarketingConsent,
  testSend: boolean,
  audienceTagSlugs: ReadonlyArray<string> = [],
): MarketingExclusionReason | null {
  if (facts.status !== "active") return "member_inactive";
  if (testSend && !facts.tagSlugs.includes(MARKETING_TEST_RECIPIENT_TAG_SLUG)) {
    return "not_test_recipient";
  }
  if (audienceTagSlugs.length > 0 && !audienceTagSlugs.some((s) => facts.tagSlugs.includes(s))) {
    return "not_in_audience_tags";
  }
  if (testSend) {
    if (facts.tagSlugs.includes(MARKETING_DO_NOT_CONTACT_TAG_SLUG)) return "internal_or_test";
  } else if (
    facts.tagSlugs.some((s) => (MARKETING_INTERNAL_TAG_SLUGS as ReadonlyArray<string>).includes(s)) ||
    (facts.email !== null && isInternalEmailAddress(facts.email))
  ) {
    return "internal_or_test";
  }
  if (consent.allMarketingOptedOut) return "marketing_opted_out";
  return null;
}

/** How an email send reads its audience. */
export interface EmailNewsletterReachOptions {
  /**
   * A test edition: only members tagged
   * {@link MARKETING_TEST_RECIPIENT_TAG_SLUG} are reachable, whatever their
   * staff tags or domain; "do not contact" and every consent rule still apply.
   */
  readonly testSend?: boolean;
  /**
   * email-newsletter-tag-audience (2026-10-31) — the tags an edition is sent
   * to: only members carrying ANY of them are reachable
   * (`not_in_audience_tags` for everyone else), then every other rule as
   * usual. Absent or empty: no tag narrows the audience. The preview, the plan
   * frozen at scheduling and each batch's re-check all pass the edition's
   * tags, so someone untagged after scheduling is skipped when their batch
   * goes, as the WhatsApp newsletter re-asks its tags.
   */
  readonly audienceTagSlugs?: ReadonlyArray<string>;
  /**
   * newsletter-welcome (2026-10-21) — the instant this email goes. Given, a
   * member who joined less than {@link NEWSLETTER_JOIN_HOLD_MS} before it is
   * held back (`joined_recently`). For an edition only the send's batch knows
   * it, so only the batch passes it: the preview and the plan frozen at
   * scheduling count such a member, and the batch skips them when it goes. A
   * test edition is never held. The newsletter-interest enroller (2026-10-23)
   * passes the instant it read its facts at, so a newsletter joiner gets the
   * sales series no sooner than their first edition.
   */
  readonly sendAt?: Date;
}

/**
 * May the email newsletter reach this member — and at which address? The
 * address is the member's CURRENT one, trimmed of spaces as the bounce check
 * trims it; the send uses it as read at send time, never a copy frozen at
 * approval.
 */
export function classifyEmailNewsletterReach(
  facts: MarketingAudienceFacts,
  options: EmailNewsletterReachOptions = {},
): MarketingReach<string> {
  const consent = readMemberMarketingConsent(facts);
  const common = commonExclusion(
    facts,
    consent,
    options.testSend === true,
    options.audienceTagSlugs,
  );
  if (common !== null) return { reachable: false, reason: common };
  if (consent.flowOptOuts.has(NEWSLETTER_FLOW_NAME)) {
    return { reachable: false, reason: "newsletter_opted_out" };
  }
  if (consent.siteConsentWithdrawn) return { reachable: false, reason: "site_consent_withdrawn" };
  if (consent.emailOptedOut) return { reachable: false, reason: "channel_opted_out" };
  const email = trimSpaces(facts.email ?? "");
  if (email.length === 0) return { reachable: false, reason: "no_address" };
  if (isPlaceholderEmail(email)) return { reachable: false, reason: "placeholder_email" };
  if (!isSendableEmailAddress(email)) return { reachable: false, reason: "no_address" };
  if (facts.emailBouncedOrComplained) {
    return { reachable: false, reason: "bounced_or_complained" };
  }
  // Last, after every lasting reason, so a member left out for good is
  // counted under that reason: this one only ever delays.
  if (
    options.sendAt !== undefined &&
    options.testSend !== true &&
    options.sendAt.getTime() - facts.createdAt.getTime() < NEWSLETTER_JOIN_HOLD_MS
  ) {
    return { reachable: false, reason: "joined_recently" };
  }
  return { reachable: true, address: email };
}

/**
 * What a studio's WhatsApp marketing leaves out by its own choice rather than
 * by a member's facts (deliverability-ladder D7, 2026-10-31). `skipUsNumbers`
 * is `studio.whatsapp_marketing_skip_us_numbers`, read `=== true`: while Meta
 * does not deliver marketing templates to US numbers ({@link isUsPhoneNumber}),
 * a studio with it on leaves them out as `us_number`. Every caller passes the
 * studio's policy, so no reach is ever counted without it.
 */
export interface WhatsAppMarketingPolicy {
  readonly skipUsNumbers: boolean;
}

/**
 * May a WhatsApp marketing send reach this member — and on which number?
 * `resolveNumber` is the WhatsApp senders' own resolver
 * (`resolveSendableWhatsappNumber`, `@layer/saga-runtime`), passed in so this
 * leaf stays free of runtime imports. The newsletter opt-out is NOT applied:
 * whether an email newsletter unsubscribe also stops the monthly WhatsApp is
 * decided with Deploy 3.
 */
export function classifyWhatsAppMarketingReach(
  facts: MarketingAudienceFacts,
  resolveNumber: (candidate: string | null) => string | null,
  policy: WhatsAppMarketingPolicy,
): MarketingReach<string> {
  const consent = readMemberMarketingConsent(facts);
  const common = commonExclusion(facts, consent, false);
  if (common !== null) return { reachable: false, reason: common };
  if (consent.siteConsentWithdrawn) return { reachable: false, reason: "site_consent_withdrawn" };
  if (consent.whatsappOptedOut) return { reachable: false, reason: "channel_opted_out" };
  for (const candidate of facts.whatsappNumbers) {
    const number = resolveNumber(candidate);
    if (number === null) continue;
    // The number it would go to, not any other candidate: a send has no
    // fallback number, so a pause on the one it uses is the one that counts.
    if (facts.pausedWhatsAppNumbers.includes(number)) {
      return { reachable: false, reason: "number_paused" };
    }
    if (policy.skipUsNumbers && isUsPhoneNumber(number)) {
      return { reachable: false, reason: "us_number" };
    }
    return { reachable: true, address: number };
  }
  return { reachable: false, reason: "no_address" };
}

// ---------------------------------------------------------------------------
// The audience, counted.
// ---------------------------------------------------------------------------

/** One member's place in the audience. */
export interface MarketingAudienceEntry {
  readonly memberId: string;
  readonly lifecycle: MarketingLifecycle;
  readonly goal: MarketingGoal;
  readonly email: MarketingReach<string>;
  readonly whatsapp: MarketingReach<string>;
}

/**
 * Classify every member, then apply the one cross-member rule: a WhatsApp
 * number reaches ONE member per send (the first by member id keeps it; the
 * rest are `duplicate_number`), as the WhatsApp newsletter does. Email needs
 * no such rule — `lower(members.email)` is unique per studio (migration 081).
 */
export function classifyMarketingAudience(
  members: ReadonlyArray<MarketingAudienceFacts>,
  now: Date,
  resolveWhatsAppNumber: (candidate: string | null) => string | null,
  whatsappPolicy: WhatsAppMarketingPolicy,
  emailOptions: EmailNewsletterReachOptions = {},
): ReadonlyArray<MarketingAudienceEntry> {
  const byId = [...members].sort((a, b) =>
    a.memberId < b.memberId ? -1 : a.memberId > b.memberId ? 1 : 0,
  );
  const claimedNumbers = new Set<string>();
  return byId.map((facts) => {
    let whatsapp = classifyWhatsAppMarketingReach(facts, resolveWhatsAppNumber, whatsappPolicy);
    if (whatsapp.reachable) {
      if (claimedNumbers.has(whatsapp.address)) {
        whatsapp = { reachable: false, reason: "duplicate_number" };
      } else {
        claimedNumbers.add(whatsapp.address);
      }
    }
    return {
      memberId: facts.memberId,
      lifecycle: classifyMarketingLifecycle(facts, now),
      goal: classifyMarketingGoal(facts),
      email: classifyEmailNewsletterReach(facts, emailOptions),
      whatsapp,
    };
  });
}

/** A zeroed count per exclusion reason. */
export function emptyExclusionCounts(): Record<MarketingExclusionReason, number> {
  const out = {} as Record<MarketingExclusionReason, number>;
  for (const r of MARKETING_EXCLUSION_REASONS) out[r] = 0;
  return out;
}

/** One cell of the audiences matrix: a lifecycle × goal. */
export interface MarketingAudienceCell {
  readonly lifecycle: MarketingLifecycle;
  readonly goal: MarketingGoal;
  readonly members: number;
  readonly emailReachable: number;
  readonly whatsappReachable: number;
}

/** The audiences matrix: every lifecycle × goal cell, plus exclusions per channel. */
export interface MarketingAudienceMatrix {
  readonly cells: ReadonlyArray<MarketingAudienceCell>;
  readonly totals: {
    readonly members: number;
    readonly emailReachable: number;
    readonly whatsappReachable: number;
  };
  readonly excluded: {
    readonly email: Readonly<Record<MarketingExclusionReason, number>>;
    readonly whatsapp: Readonly<Record<MarketingExclusionReason, number>>;
  };
}

export function countMarketingAudienceMatrix(
  entries: ReadonlyArray<MarketingAudienceEntry>,
): MarketingAudienceMatrix {
  const cells = new Map<string, { members: number; email: number; whatsapp: number }>();
  for (const l of MARKETING_LIFECYCLES) {
    for (const g of MARKETING_GOALS) cells.set(`${l}:${g}`, { members: 0, email: 0, whatsapp: 0 });
  }
  const excludedEmail = emptyExclusionCounts();
  const excludedWhatsApp = emptyExclusionCounts();
  let email = 0;
  let whatsapp = 0;
  for (const e of entries) {
    const cell = cells.get(`${e.lifecycle}:${e.goal}`);
    if (cell === undefined) continue;
    cell.members += 1;
    if (e.email.reachable) {
      cell.email += 1;
      email += 1;
    } else {
      excludedEmail[e.email.reason] += 1;
    }
    if (e.whatsapp.reachable) {
      cell.whatsapp += 1;
      whatsapp += 1;
    } else {
      excludedWhatsApp[e.whatsapp.reason] += 1;
    }
  }
  const out: MarketingAudienceCell[] = [];
  for (const l of MARKETING_LIFECYCLES) {
    for (const g of MARKETING_GOALS) {
      const c = cells.get(`${l}:${g}`);
      if (c === undefined) continue;
      out.push({
        lifecycle: l,
        goal: g,
        members: c.members,
        emailReachable: c.email,
        whatsappReachable: c.whatsapp,
      });
    }
  }
  return {
    cells: out,
    totals: { members: entries.length, emailReachable: email, whatsappReachable: whatsapp },
    excluded: { email: excludedEmail, whatsapp: excludedWhatsApp },
  };
}

/** `member:weight_loss` — the key an audience × goal is filed under. */
export function marketingAudienceKey(lifecycle: MarketingLifecycle, goal: MarketingGoal): string {
  return `${lifecycle}:${goal}`;
}
